import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import EventDivisionsManager from './EventDivisionsManager';
import {
  BarChart3,
  Link2,
  Unlink,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Settings,
  Database,
  Users,
  Trophy,
  ChevronRight,
  Search,
  Zap,
  Download,
  FileSpreadsheet,
  ExternalLink,
  ArrowRight,
  Package,
  Layers,
  ArrowUpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SportsVisio Admin Manager
 * 
 * Allows admins to:
 * 1. Check integration status
 * 2. Link Rooam events to SportsVisio PED (Program/Event/Division)
 * 3. Link Rooam athletes to SportsVisio players
 * 4. Manually trigger stat syncs
 */

const SportsVisioManager = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [manageDivisionsEvent, setManageDivisionsEvent] = useState(null);
  const [pushingEventId, setPushingEventId] = useState(null);
  const [pushAsDivisionEvent, setPushAsDivisionEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [showEventLinkDialog, setShowEventLinkDialog] = useState(false);
  const [showAthleteLinkDialog, setShowAthleteLinkDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch integration status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sportsvisio/status`);
      if (res.ok) {
        setStatus(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch SportsVisio status:', err);
    }
    setLoading(false);
  }, []);

  // Fetch Rooam events and athletes
  const fetchRooamData = useCallback(async () => {
    try {
      const [eventsRes, athletesRes] = await Promise.all([
        fetch(`${API_URL}/api/events`),
        fetch(`${API_URL}/api/athletes`)
      ]);
      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (athletesRes.ok) setAthletes(await athletesRes.json());
    } catch (err) {
      console.error('Failed to fetch Rooam data:', err);
    }
  }, []);

  // Fetch SportsVisio programs (when connected)
  const fetchPrograms = useCallback(async () => {
    if (!status?.connected) return;
    setLoadingPrograms(true);
    try {
      const res = await fetch(`${API_URL}/api/sportsvisio/programs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrograms(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch programs:', err);
    }
    setLoadingPrograms(false);
  }, [status?.connected, token]);

  useEffect(() => {
    fetchStatus();
    fetchRooamData();
  }, [fetchStatus, fetchRooamData]);

  useEffect(() => {
    if (status?.connected) {
      fetchPrograms();
    }
  }, [status?.connected, fetchPrograms]);

  // Unlink event from SportsVisio
  const handleUnlinkEvent = async (eventId) => {
    if (!window.confirm('Unlink this event from SportsVisio? You can relink it later.')) return;
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/sportsvisio/link`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Event unlinked from SportsVisio');
        fetchRooamData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to unlink event');
      }
    } catch (err) {
      toast.error('Error unlinking event');
    }
  };

  // Push Rooam divisions up to SportsVisio for this event
  const handlePushDivisions = async (eventId) => {
    setPushingEventId(eventId);
    const toastId = toast.loading('Pushing divisions to SportsVisio...');
    try {
      const res = await fetch(`${API_URL}/api/sportsvisio/events/${eventId}/push-divisions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(
          `Created ${data.created} · Matched ${data.skipped} existing`,
          { id: toastId, description: data.errors > 0 ? `${data.errors} error(s) — check logs` : 'Divisions synced' }
        );
        fetchRooamData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Push failed', { id: toastId });
      }
    } catch {
      toast.error('Push failed', { id: toastId });
    }
    setPushingEventId(null);
  };

  // Link event to SportsVisio
  const handleLinkEvent = async (eventId, svConfig) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/sportsvisio/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(svConfig)
      });
      if (res.ok) {
        const data = await res.json();
        const msg = data.games_imported > 0
          ? `Event linked — ${data.games_imported} game(s) imported from SportsVisio`
          : 'Event linked to SportsVisio';
        toast.success(msg);
        setShowEventLinkDialog(false);
        fetchRooamData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to link event');
      }
    } catch (err) {
      toast.error('Error linking event');
    }
  };

  // Link athlete to SportsVisio player
  const handleLinkAthlete = async (athleteId, svConfig) => {
    try {
      const res = await fetch(`${API_URL}/api/athletes/${athleteId}/sportsvisio/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(svConfig)
      });
      if (res.ok) {
        toast.success('Athlete linked to SportsVisio player');
        setShowAthleteLinkDialog(false);
        fetchRooamData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to link athlete');
      }
    } catch (err) {
      toast.error('Error linking athlete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-rooam-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Integration Status Card */}
      <Card className="bg-rooam-gray-medium border-rooam-gray-light">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status?.connected ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <Zap className={`h-5 w-5 ${status?.connected ? 'text-green-400' : 'text-red-400'}`} />
              </div>
              <div>
                <CardTitle className="text-white text-lg">SportsVisio Integration</CardTitle>
                <CardDescription className="text-gray-400">
                  AI-powered stats, highlights, and analytics
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchStatus}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {status?.connected ? (
              <>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
                {status?.user && (
                  <span className="text-sm text-gray-400">
                    Authenticated as: <span className="text-white">{status.user}</span>
                  </span>
                )}
              </>
            ) : status?.configured ? (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                <AlertCircle className="h-3 w-3 mr-1" />
                Token Invalid
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <XCircle className="h-3 w-3 mr-1" />
                Not Configured
              </Badge>
            )}
          </div>

          {!status?.configured && (
            <div className="mt-4 p-4 bg-rooam-black rounded-lg border border-white/10">
              <p className="text-sm text-gray-400 mb-2">
                To enable SportsVisio integration, add your API token to the backend:
              </p>
              <code className="text-xs text-cyan-400 bg-black/50 px-2 py-1 rounded">
                SPORTSVISIO_API_TOKEN=your_jwt_token
              </code>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Transfer Hub — always visible since it exports from Rooam data */}
      {events.length > 0 && <DataTransferHub events={events} />}

      {/* Only show management sections if connected */}
      {status?.connected && (
        <>
          {/* Programs Overview */}
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Database className="h-4 w-4 text-rooam-blue" />
                SportsVisio Programs
              </CardTitle>
              <CardDescription className="text-gray-400">
                Available programs/leagues in your SportsVisio account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPrograms ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 text-rooam-blue animate-spin" />
                </div>
              ) : programs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No programs found in SportsVisio account
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {programs.map((program) => (
                    <div
                      key={program.id}
                      className="flex items-center justify-between p-3 bg-rooam-black rounded-lg border border-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <Trophy className="h-4 w-4 text-amber-400" />
                        <div>
                          <p className="text-sm font-medium text-white">{program.name}</p>
                          <p className="text-xs text-gray-500">{program.sportId || 'Multi-sport'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="text-[10px] bg-white/5 text-gray-400 border-white/10">
                          {program.meta?.games || 0} games
                        </Badge>
                        <Badge className="text-[10px] bg-white/5 text-gray-400 border-white/10">
                          {program.meta?.teams || 0} teams
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Event Linking Section */}
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-cyan-400" />
                    Event Linking
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Connect Rooam events to SportsVisio for automatic stat syncing
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {events.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    className="p-3 bg-rooam-black rounded-lg border border-white/5"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${event.sportsvisio_link ? 'bg-green-400' : 'bg-gray-600'}`} />
                      <p className="text-sm font-medium text-white">{event.name}</p>
                      {event.sportsvisio_link && (
                        <Badge className="text-[10px] bg-green-500/20 text-green-400 border-green-500/30 shrink-0">
                          Linked
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pl-4 flex-wrap">
                      {/* Always-available: Manage divisions (create/rename/delete Rooam divisions before syncing) */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => setManageDivisionsEvent(event)}
                        data-testid={`manage-divisions-${event.id}`}
                        title="Add, rename, or remove divisions for this event"
                      >
                        <Layers className="h-3 w-3 mr-1" />
                        Manage Divisions ({(event.divisions || []).length})
                      </Button>

                      {/* Always-available: push this Rooam event as ONE SV Division under a chosen SV Season */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                        onClick={() => setPushAsDivisionEvent(event)}
                        data-testid={`push-as-sv-division-${event.id}`}
                        title="Push the whole event as a single SV Division under a SV Season (e.g. 2026)"
                      >
                        <ArrowUpCircle className="h-3 w-3 mr-1" />
                        Push as SV Division
                      </Button>

                      {event.sportsvisio_link ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10"
                            onClick={() => handlePushDivisions(event.id)}
                            disabled={pushingEventId === event.id}
                            data-testid={`push-sv-divisions-${event.id}`}
                            title="Push Rooam divisions up to SportsVisio"
                          >
                            {pushingEventId === event.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ArrowUpCircle className="h-3 w-3 mr-1" />}
                            Push to SV
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-red-400 hover:bg-red-500/10"
                            data-testid={`unlink-event-${event.id}`}
                            onClick={() => handleUnlinkEvent(event.id)}
                          >
                            <Unlink className="h-3 w-3 mr-1" />
                            Unlink
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowEventLinkDialog(true);
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Relink
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowEventLinkDialog(true);
                          }}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          Link
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Athlete Linking Section */}
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Users className="h-4 w-4 text-amber-400" />
                    Athlete Linking
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Connect Rooam athlete profiles to SportsVisio player records
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search athletes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-rooam-black border-white/10 text-white text-sm"
                />
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {athletes
                  .filter(a => 
                    !searchQuery || 
                    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    a.team?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .slice(0, 10)
                  .map((athlete) => (
                    <div
                      key={athlete.id}
                      className="flex items-center justify-between p-3 bg-rooam-black rounded-lg border border-white/5"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={athlete.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`}
                          alt={athlete.name}
                          className="w-8 h-8 rounded-full border border-white/10"
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white truncate">{athlete.name}</p>
                            {athlete.sportsvisio_link && (
                              <Badge className="text-[8px] bg-green-500/20 text-green-400 border-green-500/30 px-1">
                                <CheckCircle2 className="h-2 w-2" />
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{athlete.position} • {athlete.team}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {athlete.sportsvisio_link ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-red-400 hover:bg-red-500/10"
                          >
                            <Unlink className="h-3 w-3" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                            onClick={() => {
                              setSelectedAthlete(athlete);
                              setShowAthleteLinkDialog(true);
                            }}
                          >
                            <Link2 className="h-3 w-3 mr-1" />
                            Link
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Event Link Dialog */}
      <EventLinkDialog
        open={showEventLinkDialog}
        onOpenChange={setShowEventLinkDialog}
        event={selectedEvent}
        programs={programs}
        onLink={handleLinkEvent}
      />

      {/* Athlete Link Dialog */}
      <AthleteLinkDialog
        open={showAthleteLinkDialog}
        onOpenChange={setShowAthleteLinkDialog}
        athlete={selectedAthlete}
        programs={programs}
        onLink={handleLinkAthlete}
      />

      {/* Manage Divisions dialog - reuse the existing EventDivisionsManager */}
      <Dialog open={!!manageDivisionsEvent} onOpenChange={(o) => !o && setManageDivisionsEvent(null)}>
        <DialogContent className="bg-rooam-gray-dark border-rooam-gray-light text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-divisions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-emerald-400" />
              {manageDivisionsEvent?.name} — Divisions & Teams
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Add new divisions (e.g. 15U, 16U, 17U) here. After linking the event to SportsVisio, use the <span className="text-cyan-400">"Push to SV"</span> button to create them in SportsVisio.
            </DialogDescription>
          </DialogHeader>
          {manageDivisionsEvent && (
            <EventDivisionsManager
              eventId={manageDivisionsEvent.id}
              token={token}
              onUpdate={fetchRooamData}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Push Event as SV Division dialog */}
      <PushEventAsDivisionDialog
        event={pushAsDivisionEvent}
        programs={programs}
        token={token}
        onClose={() => setPushAsDivisionEvent(null)}
        onSuccess={() => {
          setPushAsDivisionEvent(null);
          fetchRooamData();
        }}
      />
    </div>
  );
};

/**
 * Data Transfer Hub — Download CSVs for SportsVisio import
 */
const DataTransferHub = ({ events }) => {
  const [selectedEventId, setSelectedEventId] = useState(events[0]?.id || '');
  const [downloading, setDownloading] = useState(null);

  const downloadCSV = async (type) => {
    if (!selectedEventId) {
      toast.error('Select an event first');
      return;
    }
    setDownloading(type);
    try {
      const res = await fetch(`${API_URL}/api/sportsvisio/export/${selectedEventId}/${type}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sportsvisio_${type}_${selectedEventId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} CSV downloaded`);
    } catch (err) {
      toast.error(`Failed to download ${type} CSV`);
    }
    setDownloading(null);
  };

  const exports = [
    { type: 'divisions', label: 'Divisions', icon: Package, desc: 'Age groups & pools' },
    { type: 'teams', label: 'Teams', icon: Users, desc: 'All team names & divisions' },
    { type: 'games', label: 'Games / Schedule', icon: FileSpreadsheet, desc: 'Full schedule with scores' },
    { type: 'rosters', label: 'Rosters', icon: Trophy, desc: 'Player names & jersey numbers' },
  ];

  return (
    <Card className="bg-rooam-gray-medium border-rooam-gray-light" data-testid="data-transfer-hub">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Download className="h-4 w-4 text-emerald-400" />
              SportsVisio Data Transfer
            </CardTitle>
            <CardDescription className="text-gray-400">
              Export your event data as CSVs to import into SportsVisio
            </CardDescription>
          </div>
          <a
            href="https://app.sportsvisio.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            Open SportsVisio
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Event selector */}
        <div>
          <Label className="text-gray-300 text-sm">Select Event to Export</Label>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="mt-1 w-full bg-rooam-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid="export-event-select"
          >
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        </div>

        {/* Export buttons grid */}
        <div className="grid grid-cols-2 gap-2">
          {exports.map(({ type, label, icon: Icon, desc }) => (
            <button
              key={type}
              onClick={() => downloadCSV(type)}
              disabled={!!downloading}
              data-testid={`export-${type}-btn`}
              className="flex items-start gap-3 p-3 bg-rooam-black rounded-lg border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all text-left disabled:opacity-50"
            >
              <div className="p-1.5 rounded bg-emerald-500/10 mt-0.5">
                {downloading === type ? (
                  <Loader2 className="h-4 w-4 text-emerald-400 animate-spin" />
                ) : (
                  <Icon className="h-4 w-4 text-emerald-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-white">{label}</p>
                <p className="text-[11px] text-gray-500">{desc}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Import instructions */}
        <div className="p-4 bg-rooam-black rounded-lg border border-amber-500/20">
          <p className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            How to Import into SportsVisio
          </p>
          <ol className="text-xs text-gray-400 space-y-1.5 list-decimal list-inside">
            <li>Download all 4 CSVs above for your event</li>
            <li>
              Log in to{' '}
              <a href="https://app.sportsvisio.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-300">
                app.sportsvisio.com
              </a>
            </li>
            <li>Go to your <strong className="text-white">Program</strong> <ArrowRight className="inline h-3 w-3" /> <strong className="text-white">Event</strong></li>
            <li>Create <strong className="text-white">Divisions</strong> matching the division names in the CSV</li>
            <li>Import <strong className="text-white">Teams</strong> into each division</li>
            <li>Upload <strong className="text-white">Rosters</strong> (player names) for each team</li>
            <li>Import the <strong className="text-white">Game Schedule</strong> — match times & courts</li>
            <li>Once imported, come back here and <strong className="text-white">Link</strong> your event above</li>
          </ol>
          <p className="text-[11px] text-gray-500 mt-2 italic">
            SportsVisio's AI needs this data to auto-generate stats from your game recordings.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};


/**
 * Dialog for linking an event to SportsVisio PED
 */
const EventLinkDialog = ({ open, onOpenChange, event, programs, onLink }) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSvEvent, setSelectedSvEvent] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCreateEventForm, setShowCreateEventForm] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventType, setNewEventType] = useState('tournament-play');
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [localSvEvents, setLocalSvEvents] = useState([]);  // events created during this session

  // Pre-fill the new-event-name field with the Rooam event name for convenience
  useEffect(() => {
    if (event?.name) setNewEventName(event.name);
  }, [event?.name, showCreateEventForm]);

  const handleCreateSvEvent = async () => {
    if (!selectedProgram) {
      toast.error('Select a Program first');
      return;
    }
    if (!newEventName.trim()) {
      toast.error('Event name is required');
      return;
    }
    setCreatingEvent(true);
    const toastId = toast.loading('Creating event in SportsVisio...');
    try {
      // Convert Rooam event dates (if present) to epoch seconds
      let startTime = null;
      let endTime = null;
      try {
        if (event?.date) startTime = Math.floor(new Date(event.date).getTime() / 1000);
        if (event?.end_date) endTime = Math.floor(new Date(event.end_date).getTime() / 1000);
      } catch (_) { /* noop */ }

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/sportsvisio/events/create-in-sportsvisio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_id: selectedProgram.id,
          name: newEventName.trim(),
          start_time: startTime,
          end_time: endTime,
          event_type: newEventType,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created "${data.event.name}" in SportsVisio`, { id: toastId });
        // Add it to the local event list so it's immediately selectable
        setLocalSvEvents([...localSvEvents, data.event]);
        setSelectedSvEvent(data.event);
        setShowCreateEventForm(false);
      } else {
        const err = await res.json().catch(() => ({}));
        // Friendlier message + actionable link when SV API doesn't support remote creation
        toast.error(
          (err.detail || 'SportsVisio API does not allow event creation from Rooam yet.') +
          ' Open SportsVisio web manager to add it, then refresh.',
          {
            id: toastId,
            duration: 8000,
            action: {
              label: 'Open SV Manager',
              onClick: () => window.open('https://app.sportsvisio.com', '_blank'),
            },
          }
        );
      }
    } catch {
      toast.error('Create failed', { id: toastId });
    }
    setCreatingEvent(false);
  };

  const handleSubmit = async () => {
    if (!selectedProgram || !selectedSvEvent) {
      toast.error('Please select Program and Event');
      return;
    }

    setSaving(true);
    await onLink(event.id, {
      program_id: selectedProgram.id,
      event_id: selectedSvEvent.id,
      division_id: selectedDivision?.id || null,
      program_name: selectedProgram.name,
      event_name: selectedSvEvent.name,
      division_name: selectedDivision?.name || null
    });
    setSaving(false);
  };

  // Get events from selected program - merge SV-returned events with locally-created ones
  const svEvents = [
    ...(selectedProgram?.programEventAssn?.map(pea => pea.event) || []),
    ...localSvEvents,
  ];
  
  // Get divisions from selected event
  const divisions = selectedProgram?.programEventAssn
    ?.find(pea => pea.event.id === selectedSvEvent?.id)
    ?.programEventDivisionAssn?.map(peda => peda.division) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Link Event to SportsVisio</DialogTitle>
          <DialogDescription className="text-gray-400">
            Connect "{event?.name}" to a SportsVisio Program/Event/Division
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Select Program */}
          <div>
            <Label className="text-gray-300 text-sm">1. Select Program</Label>
            {programs.length === 0 ? (
              <div className="mt-2 p-4 rounded-lg bg-rooam-black border border-white/10 text-center">
                <p className="text-zinc-400 text-sm mb-2">No programs found in your SportsVisio account.</p>
                <p className="text-zinc-500 text-xs">Create a program in your <a href="https://app.sportsvisio.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-300">SportsVisio dashboard</a> first, then come back to link it.</p>
              </div>
            ) : (
              <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                {programs.map((prog) => (
                  <button
                    key={prog.id}
                    onClick={() => {
                      setSelectedProgram(prog);
                      setSelectedSvEvent(null);
                      setSelectedDivision(null);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      selectedProgram?.id === prog.id
                        ? 'bg-cyan-500/20 border border-cyan-500 text-white'
                        : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    {prog.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Step 2: Select Event */}
          {selectedProgram && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-gray-300 text-sm">2. Select Event/Season</Label>
                <button
                  type="button"
                  onClick={() => setShowCreateEventForm(!showCreateEventForm)}
                  className="text-[11px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  data-testid="toggle-create-sv-event"
                >
                  {showCreateEventForm ? '− Cancel' : "+ Create new event"}
                </button>
              </div>

              {/* Inline create-event form */}
              {showCreateEventForm && (
                <div className="mt-2 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/30 space-y-2" data-testid="create-sv-event-form">
                  <p className="text-[11px] text-cyan-300">
                    Creates a new event in SportsVisio under <span className="font-semibold">{selectedProgram.name}</span>. Uses the Rooam event's name and dates by default.
                  </p>
                  <p className="text-[10px] text-amber-400/80 leading-relaxed">
                    ⚠️ SportsVisio may not expose remote event-creation on your API plan. If this fails, click <span className="underline">Open SV Manager</span> in the toast to add the event manually, then refresh the page.
                  </p>
                  <Input
                    value={newEventName}
                    onChange={(e) => setNewEventName(e.target.value)}
                    placeholder="Event name"
                    className="bg-rooam-black border-white/10 text-white text-sm h-9"
                    data-testid="new-sv-event-name"
                  />
                  <select
                    value={newEventType}
                    onChange={(e) => setNewEventType(e.target.value)}
                    className="w-full bg-rooam-black border border-white/10 text-white text-sm rounded-md h-9 px-3"
                    data-testid="new-sv-event-type"
                  >
                    <option value="tournament-play">Tournament Play</option>
                    <option value="in-league-play">In League Play</option>
                    <option value="in-season-play">In Season Play</option>
                  </select>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCreateSvEvent}
                      disabled={creatingEvent || !newEventName.trim()}
                      size="sm"
                      className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white text-xs h-8"
                      data-testid="submit-create-sv-event"
                    >
                      {creatingEvent ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                        <>
                          <ArrowUpCircle className="h-3 w-3 mr-1" />
                          Try via API
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 text-xs h-8"
                      onClick={() => window.open('https://app.sportsvisio.com', '_blank')}
                      data-testid="open-sv-manager"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Open SV Manager
                    </Button>
                  </div>
                </div>
              )}

              {svEvents.length === 0 && !showCreateEventForm ? (
                <div className="mt-2 p-3 rounded-lg bg-rooam-black border border-white/10 text-center">
                  <p className="text-zinc-400 text-xs">No events under this program yet.</p>
                  <p className="text-zinc-500 text-[11px] mt-1">Click <span className="text-cyan-400">"+ Create new event"</span> above.</p>
                </div>
              ) : (
                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                  {svEvents.map((evt) => (
                    <button
                      key={evt.id}
                      onClick={() => {
                        setSelectedSvEvent(evt);
                        setSelectedDivision(null);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        selectedSvEvent?.id === evt.id
                          ? 'bg-cyan-500/20 border border-cyan-500 text-white'
                          : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                      }`}
                    >
                      {evt.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Division */}
          {selectedSvEvent && divisions.length > 0 && (
            <div>
              <Label className="text-gray-300 text-sm">3. Select Division</Label>
              <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                {divisions.map((div) => (
                  <button
                    key={div.id}
                    onClick={() => setSelectedDivision(div)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      selectedDivision?.id === div.id
                        ? 'bg-cyan-500/20 border border-cyan-500 text-white'
                        : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    {div.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={saving || !selectedProgram || !selectedSvEvent || programs.length === 0}
            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Link Event
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Dialog for linking an athlete to SportsVisio player
 */
const AthleteLinkDialog = ({ open, onOpenChange, athlete, programs, onLink }) => {
  const [svPlayerId, setSvPlayerId] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSvEvent, setSelectedSvEvent] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!svPlayerId || !selectedProgram || !selectedSvEvent || !selectedDivision) {
      toast.error('Please fill all fields');
      return;
    }

    setSaving(true);
    await onLink(athlete.id, {
      sportsvisio_player_id: svPlayerId,
      sportsvisio_program_id: selectedProgram.id,
      sportsvisio_event_id: selectedSvEvent.id,
      sportsvisio_division_id: selectedDivision.id
    });
    setSaving(false);
  };

  const svEvents = selectedProgram?.programEventAssn?.map(pea => pea.event) || [];
  const divisions = selectedProgram?.programEventAssn
    ?.find(pea => pea.event.id === selectedSvEvent?.id)
    ?.programEventDivisionAssn?.map(peda => peda.division) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Link Athlete to SportsVisio</DialogTitle>
          <DialogDescription className="text-gray-400">
            Connect "{athlete?.name}" to their SportsVisio player profile
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* SportsVisio Player ID */}
          <div>
            <Label className="text-gray-300 text-sm">SportsVisio Player ID</Label>
            <Input
              value={svPlayerId}
              onChange={(e) => setSvPlayerId(e.target.value)}
              placeholder="e.g., abc123-def456-ghi789"
              className="mt-1 bg-rooam-black border-white/10 text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Find this in SportsVisio's player roster
            </p>
          </div>

          {/* Program Selection */}
          <div>
            <Label className="text-gray-300 text-sm">Program</Label>
            <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
              {programs.map((prog) => (
                <button
                  key={prog.id}
                  onClick={() => {
                    setSelectedProgram(prog);
                    setSelectedSvEvent(null);
                    setSelectedDivision(null);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                    selectedProgram?.id === prog.id
                      ? 'bg-amber-500/20 border border-amber-500 text-white'
                      : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                  }`}
                >
                  {prog.name}
                </button>
              ))}
            </div>
          </div>

          {/* Event Selection */}
          {selectedProgram && svEvents.length > 0 && (
            <div>
              <Label className="text-gray-300 text-sm">Event</Label>
              <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
                {svEvents.map((evt) => (
                  <button
                    key={evt.id}
                    onClick={() => {
                      setSelectedSvEvent(evt);
                      setSelectedDivision(null);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      selectedSvEvent?.id === evt.id
                        ? 'bg-amber-500/20 border border-amber-500 text-white'
                        : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    {evt.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Division Selection */}
          {selectedSvEvent && divisions.length > 0 && (
            <div>
              <Label className="text-gray-300 text-sm">Division</Label>
              <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
                {divisions.map((div) => (
                  <button
                    key={div.id}
                    onClick={() => setSelectedDivision(div)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      selectedDivision?.id === div.id
                        ? 'bg-amber-500/20 border border-amber-500 text-white'
                        : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    {div.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={saving || !svPlayerId || !selectedProgram || !selectedSvEvent || !selectedDivision}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Link Athlete
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Dialog to push a whole Rooam event as a single SV Division under a chosen SV Season.
 * Hierarchy:  SV Program -> SV Season (event) -> SV Division (= Rooam event name)
 */
const PushEventAsDivisionDialog = ({ event, programs, token, onClose, onSuccess }) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSvEvent, setSelectedSvEvent] = useState(null);
  const [divisionName, setDivisionName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setDivisionName(event.name || '');
      setSelectedProgram(null);
      setSelectedSvEvent(null);
    }
  }, [event]);

  if (!event) return null;

  const svEvents = selectedProgram?.programEventAssn?.map(pea => pea.event) || [];

  const handleSubmit = async () => {
    if (!selectedProgram || !selectedSvEvent) {
      toast.error('Select a Program and a SV Season');
      return;
    }
    if (!divisionName.trim()) {
      toast.error('Division name is required');
      return;
    }
    setSaving(true);
    const toastId = toast.loading('Pushing event to SportsVisio as a Division...');
    try {
      const res = await fetch(`${API_URL}/api/sportsvisio/events/${event.id}/push-as-division`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_id: selectedProgram.id,
          sv_event_id: selectedSvEvent.id,
          division_name: divisionName.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.message || 'Event pushed as SV Division', {
          id: toastId,
          description: data.matched_existing ? 'Matched an existing SV division.' : 'New SV division created.',
        });
        onSuccess?.();
      } else {
        toast.error(data.detail || 'Push failed', { id: toastId });
      }
    } catch {
      toast.error('Push failed', { id: toastId });
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg" data-testid="push-as-sv-division-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-fuchsia-400" />
            Push "{event.name}" as SV Division
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Creates a new SportsVisio <span className="text-fuchsia-300">Division</span> named after this event, under a parent SV <span className="text-cyan-300">Season</span> (e.g. "2026"). All Rooam teams in the event will live under that one SV Division.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Program */}
          <div>
            <Label className="text-gray-300 text-sm">1. Select SV Program</Label>
            {programs.length === 0 ? (
              <div className="mt-2 p-3 rounded-lg bg-rooam-black border border-white/10 text-center">
                <p className="text-zinc-400 text-xs">No SV programs returned from the API.</p>
              </div>
            ) : (
              <div className="mt-2 max-h-28 overflow-y-auto space-y-1">
                {programs.map((prog) => (
                  <button
                    key={prog.id}
                    onClick={() => { setSelectedProgram(prog); setSelectedSvEvent(null); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      selectedProgram?.id === prog.id
                        ? 'bg-fuchsia-500/20 border border-fuchsia-500 text-white'
                        : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                    }`}
                    data-testid={`pad-program-${prog.id}`}
                  >
                    {prog.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Season / SV Event */}
          {selectedProgram && (
            <div>
              <Label className="text-gray-300 text-sm">
                2. Select SV Season <span className="text-gray-500 text-[11px]">(SV calls this an "Event" internally — e.g. 2026)</span>
              </Label>
              {svEvents.length === 0 ? (
                <div className="mt-2 p-3 rounded-lg bg-rooam-black border border-white/10 text-center">
                  <p className="text-zinc-400 text-xs">No Seasons under this Program. Create one in SportsVisio first.</p>
                </div>
              ) : (
                <div className="mt-2 max-h-28 overflow-y-auto space-y-1">
                  {svEvents.map((evt) => (
                    <button
                      key={evt.id}
                      onClick={() => setSelectedSvEvent(evt)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        selectedSvEvent?.id === evt.id
                          ? 'bg-fuchsia-500/20 border border-fuchsia-500 text-white'
                          : 'bg-rooam-black border border-white/10 text-gray-300 hover:bg-white/5'
                      }`}
                      data-testid={`pad-sv-event-${evt.id}`}
                    >
                      {evt.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Division name */}
          <div>
            <Label className="text-gray-300 text-sm">3. SV Division Name</Label>
            <Input
              value={divisionName}
              onChange={(e) => setDivisionName(e.target.value)}
              placeholder="e.g. Elbert Kinnebrew Cali Classic"
              className="mt-1 bg-rooam-black border-white/10 text-white text-sm"
              data-testid="pad-division-name"
            />
            <p className="text-[11px] text-gray-500 mt-1">Defaults to the Rooam event name.</p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving || !selectedProgram || !selectedSvEvent || !divisionName.trim()}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="pad-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                <ArrowUpCircle className="h-4 w-4 mr-2" />
                Push as SV Division
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SportsVisioManager;
